'use client'

import { motion } from "motion/react";
import React, { CSSProperties, JSX, ReactNode, useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

type PopupProps<T extends keyof JSX.IntrinsicElements> = {
    as?: T;
    children?: ReactNode;
    distance?: number;
    horizontal?: 'auto' | 'left' | 'right';
    vertical?: 'auto' | 'top' | 'bottom';
    priority?: 'horizontal' | 'vertical';
    onClose: () => void;
} & JSX.IntrinsicElements[T];

function Popup<T extends keyof JSX.IntrinsicElements = 'div'>({
    distance = 6,
    horizontal = 'auto',
    vertical = 'auto',
    priority = 'horizontal',
    onClose,
    as = 'div' as T,
    children,
    ...props
}: PopupProps<T>) {
    props.className = props.className || "flex flex-col gap-3 p-3 text-sm bg-lightmode-primary dark:bg-darkmode-primary border border-lightmode-primary-accent dark:border-darkmode-primary-accent rounded-md shadow-md";

    const popupRef = useRef<HTMLDivElement>(null);
    const coreRef = useRef<HTMLDivElement>(null);
    const [configuration, setConfiguration] = useState<{
        start: Pick<CSSProperties, 'left' | 'top' | 'right' | 'bottom' | 'transform' | 'opacity'>;
        end: Pick<CSSProperties, 'left' | 'top' | 'right' | 'bottom' | 'transform' | 'opacity'>;
    }>();

    const calcConfiguration = useCallback(() => {
        if (!coreRef.current) return;

        const startSize = coreRef.current.getBoundingClientRect();
        const popupSize = popupRef.current?.getBoundingClientRect();

        const windowX = window.innerWidth;
        const windowY = window.innerHeight;

        const popupWidth = popupSize?.width ?? 0;
        const popupHeight = popupSize?.height ?? 0;

        let resolvedHorizontal = horizontal;
        let resolvedVertical = vertical;

        const fitsLeft = startSize.left - distance - popupWidth >= 0;
        const fitsRight = startSize.right + distance + popupWidth <= windowX;
        const fitsTop = startSize.top - distance - popupHeight >= 0;
        const fitsBottom = startSize.bottom + distance + popupHeight <= windowY;

        const chooseHorizontal = () => {
            if (resolvedHorizontal === 'auto') {
                return windowX * 0.5 < startSize.left + startSize.width * 0.5 ? 'left' : 'right';
            }

            if (resolvedHorizontal === 'left' && !fitsLeft && fitsRight) {
                return 'right';
            }

            if (resolvedHorizontal === 'right' && !fitsRight && fitsLeft) {
                return 'left';
            }

            return resolvedHorizontal;
        };

        const chooseVertical = () => {
            if (resolvedVertical === 'auto') {
                return windowY * 0.5 < startSize.top + startSize.height * 0.5 ? 'top' : 'bottom';
            }

            if (resolvedVertical === 'top' && !fitsTop && fitsBottom) {
                return 'bottom';
            }

            if (resolvedVertical === 'bottom' && !fitsBottom && fitsTop) {
                return 'top';
            }

            return resolvedVertical;
        };

        resolvedHorizontal = chooseHorizontal();
        resolvedVertical = chooseVertical();

        const start: Pick<CSSProperties, 'left' | 'top' | 'right' | 'bottom' | 'transform' | 'opacity'> = { opacity: 0 };
        const end: Pick<CSSProperties, 'left' | 'top' | 'right' | 'bottom' | 'transform' | 'opacity'> = { opacity: 1 };

        switch (priority) {
            case 'horizontal':
                switch (resolvedHorizontal) {
                    case 'left':
                        start.right = windowX - startSize.right;
                        end.right = windowX - startSize.left + distance;
                        break;

                    case 'right':
                        start.left = startSize.left;
                        end.left = startSize.right + distance;
                        break;
                }

                switch (resolvedVertical) {
                    case 'top':
                        start.bottom = end.bottom = windowY - startSize.bottom;
                        break;

                    case 'bottom':
                        start.top = end.top = startSize.top;
                        break;
                }

                break;

            case 'vertical':
                switch (resolvedVertical) {
                    case 'top':
                        start.bottom = windowY - startSize.bottom;
                        end.bottom = windowY - startSize.top + distance;
                        break;

                    case 'bottom':
                        start.top = startSize.top;
                        end.top = startSize.bottom + distance;
                        break;
                }

                switch (resolvedHorizontal) {
                    case 'left':
                        start.right = end.right = windowX - startSize.right;
                        break;

                    case 'right':
                        start.left = end.left = startSize.left;
                        break;
                }

                break;
        }

        // Keep searchable menus reachable when neither side can fit their full height.
        const left = typeof end.left === 'number' ? end.left : windowX - Number(end.right ?? 0) - popupWidth;
        const top = typeof end.top === 'number' ? end.top : windowY - Number(end.bottom ?? 0) - popupHeight;
        if (left < 8 || left + popupWidth > windowX - 8) { end.left = Math.max(8, Math.min(left, windowX - popupWidth - 8)); delete end.right; start.left = end.left; delete start.right; }
        if (top < 8 || top + popupHeight > windowY - 8) { end.top = Math.max(8, Math.min(top, windowY - popupHeight - 8)); delete end.bottom; start.top = end.top; delete start.bottom; }
        setConfiguration({ start, end });
    }, [horizontal, vertical, priority, distance]);

    useEffect(() => {
        const onPointerDown = (e: PointerEvent) => {
            if (
                e.target instanceof Element &&
                popupRef.current &&
                coreRef.current &&
                !popupRef.current.contains(e.target) &&
                !coreRef.current.parentElement?.contains(e.target)
            ) {
                if (e.target.closest('[data-popup]')) {
                    return;
                }

                onClose();
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || !popupRef.current) return;
            const popups = document.querySelectorAll('[data-popup]');
            if (popups[popups.length - 1] !== popupRef.current) return;

            e.preventDefault();
            e.stopPropagation();
            onClose();
        };

        // Graph controls stop propagation, so dismissal runs before their handlers.
        window.addEventListener('pointerdown', onPointerDown, true);
        window.addEventListener('keydown', onKeyDown, true);

        return () => {
            window.removeEventListener('pointerdown', onPointerDown, true);
            window.removeEventListener('keydown', onKeyDown, true);
        };
    }, [onClose]);

    useEffect(() => {
        if (!coreRef.current) return;

        let frame: number | undefined;

        const isAnchorInViewport = () => {
            if (!coreRef.current || !coreRef.current.isConnected) {
                return false;
            }

            const rect = coreRef.current.getBoundingClientRect();

            return (
                rect.bottom > 0 &&
                rect.right > 0 &&
                rect.top < window.innerHeight &&
                rect.left < window.innerWidth
            );
        };

        const updatePopupPosition = () => {
            if (frame !== undefined) return;

            frame = requestAnimationFrame(() => {
                frame = undefined;

                if (!isAnchorInViewport()) {
                    onClose();
                    return;
                }

                calcConfiguration();
            });
        };

        const resizeObserver = new ResizeObserver(updatePopupPosition);
        const mutationObserver = new MutationObserver(updatePopupPosition);
        const intersectionObserver = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) {
                    onClose();
                    return;
                }

                updatePopupPosition();
            },
            {
                root: null,
                threshold: 0,
            }
        );

        resizeObserver.observe(coreRef.current);
        mutationObserver.observe(coreRef.current, {
            attributes: true,
            childList: true,
            subtree: true,
        });
        intersectionObserver.observe(coreRef.current);

        window.addEventListener('scroll', updatePopupPosition, true);
        window.addEventListener('resize', updatePopupPosition);

        updatePopupPosition();

        return () => {
            if (frame !== undefined) {
                cancelAnimationFrame(frame);
            }

            resizeObserver.disconnect();
            mutationObserver.disconnect();
            intersectionObserver.disconnect();

            window.removeEventListener('scroll', updatePopupPosition, true);
            window.removeEventListener('resize', updatePopupPosition);
        };
    }, [calcConfiguration, onClose]);

    /*
     * The popup is rendered through a portal after the first position
     * calculation. Recalculate once it exists so automatic placement uses its
     * real dimensions rather than a temporary width and height of zero.
     */
    useEffect(() => {
        if (!configuration || !popupRef.current) return;

        const frame = requestAnimationFrame(calcConfiguration);
        const resizeObserver = new ResizeObserver(calcConfiguration);

        resizeObserver.observe(popupRef.current);

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
        };
    }, [Boolean(configuration), calcConfiguration]);

    return (
        <div data-popup-core-container ref={coreRef} className="absolute top-0 left-0 w-full h-full pointer-events-none">
            {configuration &&
                ReactDOM.createPortal(
                    <motion.div
                        key="popup"
                        data-popup
                        ref={popupRef}
                        initial={configuration.start}
                        animate={configuration.end}
                        className="fixed w-fit h-fit z-99999999"
                    >
                        {React.createElement(as, { ...props }, children)}
                    </motion.div>,
                    document.body
                )
            }
        </div>
    );
}

type PopupComponents = {
    [K in keyof JSX.IntrinsicElements]: React.FC<Omit<PopupProps<K>, "as">>;
};

const popupComponents = new Map<string, React.FC>();

export const popup: PopupComponents = new Proxy({} as PopupComponents, {
    get: (_, elementType: string) => {
        if (!popupComponents.has(elementType)) {
            const Component: React.FC = (props: any) => <Popup {...props} as={elementType} />;
            popupComponents.set(elementType, Component);
        }

        return popupComponents.get(elementType);
    },
});